import { useState, useEffect, useCallback } from 'react';
import { useAbility } from '@casl/react';
import { captureException, captureMessage } from '@sentry/react';

import { AxiosError } from 'axios';
import { decodeJWT, getJWT } from '@bynder/js-access-token-lib';
import { initialValue } from '../../context';
import { HeaderInfo, UserData } from '../../types';
import { initCollectionsDropdown, initMain } from '../../legacy/main';

import { AbilityContext } from '../../abilities/Can';
import { API } from '../api';
import { getLegacyAbilityRules, initLegacy } from '../../legacy/legacy';
import { getStaticURL } from '../../components/Spotlight/helpers/getStaticURL';
import { useMemoizedSplit } from '../useSplit';
import { getBynderAIRules } from '../parseAIPermissions';

const ANONYMOUS_USER = 'anonymous';

const getCacheInvalidationId = (portalId: string, userId: string) => {
	return userId === ANONYMOUS_USER ? portalId : `${portalId}-${userId}`;
};

const useInitHeader = () => {
	const [isLoading, setIsLoading] = useState(true);
	const [headerInfo, setHeaderInfo] = useState<HeaderInfo>(initialValue);
	const ability = useAbility(AbilityContext);
	const [lastChange, setLastChange] = useState(new Date());
	const homepageSplitEnabled = useMemoizedSplit('portal_new_homepage_rollout');
	const [homepageAppAccessEnabled, setHomepageAppAccessEnabled] = useState<boolean | null>(null);

	const [{ userId, portalId }, setUuids] = useState<{ userId: string; portalId: string }>({
		userId: '',
		portalId: '',
	});

	const getHomepageInfo = useCallback(async () => {
		let isConfigured: boolean;

		try {
			const result = await API.getPortalHomepage();

			if (result.homepage.includes('/guidelines/')) {
				isConfigured = true;
			} else {
				isConfigured = false;
			}
		} catch (error: unknown) {
			captureMessage('Unified Header | Server Error getting portal homepage');
			captureException(error);

			isConfigured = false;
		}

		setHeaderInfo((prevInfo) => ({
			...prevInfo,
			homepage: {
				...prevInfo.homepage,
				isConfigured,
			},
		}));
	}, []);

	const getHpAppAccess = useCallback(async () => {
		const jwt = await getJWT();
		const decoded = decodeJWT(jwt);

		setHomepageAppAccessEnabled(decoded?.app_codes?.includes('HP'));
	}, []);

	useEffect(() => {
		if (homepageAppAccessEnabled === null) {
			getHpAppAccess();

			return;
		}

		if (homepageAppAccessEnabled || homepageSplitEnabled) {
			getHomepageInfo();
		}

		setHeaderInfo((prevInfo) => ({
			...prevInfo,
			homepage: {
				...prevInfo.homepage,
				isEnabled: homepageAppAccessEnabled
					? homepageAppAccessEnabled
					: homepageSplitEnabled,
				isSplitEnabled: homepageSplitEnabled,
				isAppAccessEnabled: homepageAppAccessEnabled,
			},
		}));
	}, [getHomepageInfo, getHpAppAccess, homepageAppAccessEnabled, homepageSplitEnabled]);

	const getLanguages = (uuid: string) => {
		return API.getLanguages(uuid)
			.then((availableLanguages) => {
				setHeaderInfo((prevInfo) => ({
					...prevInfo,
					languages: {
						...prevInfo.languages,
						available: availableLanguages,
					},
				}));
			})
			.catch((e: AxiosError) => {
				captureMessage('Unified Header | Server Error getting languages');
				captureException(e);
			});
	};

	const getAbilities = useCallback(
		async (uuid: string, isPublic: boolean) => {
			let rules = [];
			let isImpersonating = false;

			const bynderAIRules = isPublic ? [] : await getBynderAIRules();

			const abilitiesAPI = isPublic ? API.getAbilitiesPublic : API.getAbilities;

			const promiseAbilities = abilitiesAPI(uuid).catch(() => {
				// Second try
				return abilitiesAPI(uuid).catch((e: unknown) => {
					captureMessage('Unified Header | Server Error getting abilities, second try');
					captureException(e);

					return [];
				});
			});

			const promiseImpersonate = API.getImpersonate()
				.then((result) => {
					// TODO: Remove this check after changing the endpoint behaviour
					// For public access the backed redirects and response HTML
					if (result.toString().includes('<!doctype html>')) {
						return false;
					}

					return result;
				})
				.catch(() => {
					return false;
				});

			[rules, isImpersonating] = await Promise.all([promiseAbilities, promiseImpersonate]);
			const legacyRules = getLegacyAbilityRules();

			if (isImpersonating) {
				ability.update([
					...rules,
					...legacyRules,
					...bynderAIRules,
					{ action: 'show', subject: 'exit_impersonate' },
				]);
			} else {
				ability.update([...rules, ...legacyRules, ...bynderAIRules]);
			}
		},
		[ability],
	);

	const getNavigationItems = async (uuid: string, isPublic: boolean) => {
		if (isPublic) {
			return;
		}

		const navItems = await API.getNavigation(uuid).catch(async () => {
			// Second try
			const secondTryResult = await API.getNavigation(uuid).catch((e: AxiosError) => {
				captureMessage(
					'Unified Header | Server Error getting navigation items, second try',
				);
				captureException(e);

				return [];
			});

			return secondTryResult;
		});

		setHeaderInfo((prev) => ({ ...prev, navItems }));
	};

	const getDefaultLogoUrl = () => {
		return `${getStaticURL()}/includes/img/account/bynder/logo-login.png`;
	};

	const getGeneralData = useCallback(async () => {
		const emptyPortal = { portal_id: '', name: '', logo: '', logo_link: '', domain: '' };
		const portalInfo = await API.getPortalInfo().catch(async () => {
			// Second try
			const secondTryResult = await API.getPortalInfo().catch((e: AxiosError) => {
				captureMessage('Unified Header | Server Error getting current portal, second try');
				captureException(e);

				return emptyPortal;
			});

			return secondTryResult;
		});

		setHeaderInfo((prev) => ({
			...prev,

			general: {
				...prev.general,
				accountId: portalInfo?.portal_id,
				accountName: portalInfo.name,
				portalDomain: portalInfo.domain,
			},
			logo: {
				linkUrl: portalInfo.logo_link || '/',
				imageUrl: portalInfo.logo || getDefaultLogoUrl(),
			},
		}));

		setUuids((prev) => ({ ...prev, portalId: portalInfo?.portal_id }));
	}, []);

	const getCurrentUserInfo = async () => {
		let user: UserData | null = null;

		user = await API.getCurrentUser().catch(async () => {
			// Second try
			const secondTryResult = await API.getCurrentUser().catch(() => {
				// 401 if there is no session
				return null;
			});

			return secondTryResult;
		});

		setUuids((prev) => ({ ...prev, userId: user && user.id ? user.id : ANONYMOUS_USER }));

		if (user === null) {
			setHeaderInfo((prevInfo) => ({
				...prevInfo,
				general: {
					...prevInfo.general,
					isLoggedIn: false,
					isPublic: true,
				},
			}));

			return;
		}

		setHeaderInfo((prevInfo) => ({
			...prevInfo,
			general: {
				...prevInfo.general,
				isLoggedIn: true,
				userId: user?.id || '',
				userName: user?.name || '',
				userPictureUrl: user?.avatar_url || '',
				isPublic: user?.public_user === 'True',
			},
			languages: {
				selected: user?.preferred_language || '',
				available: prevInfo.languages.available,
			},
			accounts: user?.accounts || [],
		}));
	};

	useEffect(() => {
		// Deactivate legacy initializations - TODO: remove when legacy code has been removed.
		if (typeof window.bynder?.helpers?.ToolbarHelper?.initialize === 'function') {
			window.bynder.helpers.ToolbarHelper.initialize = () => false;
		}

		const initBeforeRender = () => {
			initMain();
			initLegacy();
			initCollectionsDropdown();
			getGeneralData();
		};

		initBeforeRender();
		getCurrentUserInfo();
	}, [getGeneralData]);

	useEffect(() => {
		const areIdsDefined = userId.length > 0 && portalId.length > 0;

		if (areIdsDefined) {
			const uuid = getCacheInvalidationId(portalId, userId);
			const isPublic = userId === ANONYMOUS_USER;

			Promise.all([
				getNavigationItems(uuid, isPublic),
				getAbilities(uuid, isPublic),
				getLanguages(uuid),
			]).finally(() => {
				setIsLoading(false);
			});
		}
	}, [userId, portalId, getAbilities, getHomepageInfo]);

	useEffect(() => {
		setHeaderInfo((prevInfo) => ({
			...prevInfo,
			lastChange,
			setLastChange: () => setLastChange(new Date()),
		}));
	}, [lastChange]);

	return { isLoading, headerInfo };
};

export default useInitHeader;
